import { Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import {
	Field,
	type Rules,
	makeRules,
	validate,
} from "foldkit/fieldValidation";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineMessageUnion } from "foldkit/message";
import { namedInteractions } from "../app/namedInteractions";
import { i18next } from "../i18n";

import {
	type AttributeSchemaEntry,
	type EntityType,
	ValueType,
	normalizeEntityType,
} from "../api/schemas/attributeSchema";
import type { Shared } from "../pages/shared";
import { Plus, Trash2 } from "./icons";
import { safeUrl } from "./safeUrl";

/**
 * v1 `AttributeEditor`, shared by the Entry, Record, and Searched (add-record)
 * forms. The parent owns the rows; this module holds the pure row logic and
 * the row-list view. Reserved keys (`subtitle`, `email`) never appear here —
 * the forms carry them as their own fields.
 */

/** One row: the value is a `Field` validated per keystroke, never blocking. */
export const Row = S.Struct({
	key: S.String,
	value: Field(S.String),
	type: ValueType,
	/** Freeform row whose key is still editable (schema rows have a read-only key). */
	isNew: S.Boolean,
});
export type Row = typeof Row.Type;

export const Message = defineMessageUnion({
	ClickedAdd: {},
	ChangedKey: { index: S.Number, value: S.String },
	ChangedType: { index: S.Number, value: ValueType },
	ChangedValue: { index: S.Number, value: S.String },
	ClickedRemove: { index: S.Number },
});
export type Message = typeof Message.Type;

/** `agentLink` is its own write (Agent link control); the server drops it from every generic write. */
export const RESERVED_KEYS = new Set(["subtitle", "email", "agentLink"]);

/** The message is looked up when the rule fails, so it follows the current language. */
const rule = (
	predicate: (value: string) => boolean,
	type: "number" | "date" | "boolean" | "url",
): Rules<string> =>
	makeRules({
		rules: [
			[
				(value) => value === "" || predicate(value),
				() => i18next.t(`attributes.invalid.${type}`),
			],
		],
	});

/** v1 `validateAttributeValue`; empty is always valid. */
export const valueRules = (type: ValueType): Rules<string> => {
	switch (type) {
		case "number":
			return rule((v) => !Number.isNaN(Number(v)), "number");
		case "date":
			return rule((v) => !Number.isNaN(Date.parse(v)), "date");
		case "boolean":
			return rule(
				(v) => v.toLowerCase() === "true" || v.toLowerCase() === "false",
				"boolean",
			);
		case "url":
			return rule(
				(v) => v.startsWith("http://") || v.startsWith("https://"),
				"url",
			);
		default:
			return makeRules();
	}
};

export const validated = (type: ValueType, value: string): Field<string> =>
	validate(valueRules(type))(value);

/** v1 `buildRows`: schema rows first (sorted, reserved keys skipped), then freeform. */
export const buildRows = (
	attributes: Readonly<Record<string, string>> | undefined,
	schema: ReadonlyArray<AttributeSchemaEntry>,
): ReadonlyArray<Row> => {
	const used = new Set<string>();
	const rows: Row[] = [];
	for (const entry of [...schema].sort((a, b) => a.sortOrder - b.sortOrder)) {
		if (RESERVED_KEYS.has(entry.key)) continue;
		used.add(entry.key);
		rows.push({
			key: entry.key,
			value: validated(entry.valueType, attributes?.[entry.key] ?? ""),
			type: entry.valueType,
			isNew: false,
		});
	}
	for (const [key, value] of Object.entries(attributes ?? {})) {
		if (used.has(key) || RESERVED_KEYS.has(key)) continue;
		rows.push({
			key,
			value: validated("string", value),
			type: "string",
			isNew: false,
		});
	}
	return rows;
};

/** v1 `emitChange` + save: only rows with a key and a value are kept. */
export const flattenRows = (
	rows: ReadonlyArray<Row>,
): Record<string, string> | undefined => {
	const entries = rows
		.filter((row) => row.key.trim() !== "" && row.value.value.trim() !== "")
		.map((row) => [row.key.trim(), row.value.value] as const);
	return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

/** The root's schema entries for one entity type (KTD3). */
export const schemaFor = (
	shared: Shared,
	entityType: typeof EntityType.Type,
): ReadonlyArray<AttributeSchemaEntry> =>
	AsyncData.getOrElse(shared.attributeSchema, () => ({ data: [] })).data.filter(
		(entry) => normalizeEntityType(entry.entityType) === entityType,
	);

const rowAt = (
	rows: ReadonlyArray<Row>,
	index: number,
	f: (row: Row) => Row,
): ReadonlyArray<Row> => rows.map((row, i) => (i === index ? f(row) : row));

export const update = (
	rows: ReadonlyArray<Row>,
	message: Message,
	schema: ReadonlyArray<AttributeSchemaEntry>,
): ReadonlyArray<Row> =>
	Message.match(message, {
		ClickedAdd: () => [
			...rows,
			{ key: "", value: validated("string", ""), type: "string", isNew: true },
		],
		// v1 `handleKeyChange`: a key matching a schema entry adopts its type.
		ChangedKey: ({ index, value }) => {
			const schemaType = schema.find((s) => s.key === value)?.valueType;
			return rowAt(rows, index, (row) => ({
				...row,
				key: value,
				...(schemaType
					? { type: schemaType, value: validated(schemaType, row.value.value) }
					: {}),
			}));
		},
		ChangedType: ({ index, value }) =>
			rowAt(rows, index, (row) => ({
				...row,
				type: value,
				value: validated(value, row.value.value),
			})),
		ChangedValue: ({ index, value }) =>
			rowAt(rows, index, (row) => ({
				...row,
				value: validated(row.type, value),
			})),
		ClickedRemove: ({ index }) => rows.filter((_, i) => i !== index),
	});

// VIEW

/** Read-only rows with a non-empty value (v1 `readOnly` mode). */
export const readOnlyView = <ParentMessage>(
	h: HtmlBuilder<ParentMessage>,
	rows: ReadonlyArray<Row>,
): Html => {
	const shown = rows.filter((row) => row.value.value.trim() !== "");
	return h.div(
		[h.Class("space-y-1")],
		shown.map((row) => {
			const href = row.type === "url" ? safeUrl(row.value.value) : undefined;
			return h.keyed("div")(
				row.key,
				[h.Class("flex items-center gap-2 text-sm")],
				[
					h.span(
						[h.Class("font-medium text-base-content/70 min-w-[100px]")],
						[row.key],
					),
					href !== undefined
						? h.a(
								[
									h.Href(href),
									h.Target("_blank"),
									h.DataAttribute(
										"analytics-event",
										namedInteractions.attributeLink,
									),
									h.Rel("noopener noreferrer"),
									h.Class("link link-primary truncate"),
								],
								[row.value.value],
							)
						: row.type === "boolean"
							? h.span(
									[h.Class("badge badge-sm badge-outline")],
									[
										i18next.t(
											row.value.value.toLowerCase() === "true"
												? "attributes.yes"
												: "attributes.no",
										),
									],
								)
							: h.span([h.Class("truncate")], [row.value.value]),
				],
			);
		}),
	);
};

const valueInput = <ParentMessage>(
	h: HtmlBuilder<ParentMessage>,
	row: Row,
	index: number,
	to: (message: Message) => ParentMessage,
): Html => {
	const invalid = row.value._tag === "Invalid";
	const base = `input input-bordered input-sm w-full${invalid ? " input-error" : ""}`;
	const onInput = h.OnInput((value) =>
		to(Message.ChangedValue({ index, value })),
	);
	switch (row.type) {
		case "boolean":
			return h.input([
				h.Type("checkbox"),
				h.Class("toggle toggle-sm"),
				h.AriaLabel(i18next.t("attributes.booleanValue")),
				h.Checked(row.value.value.toLowerCase() === "true"),
				h.OnClick(
					to(
						Message.ChangedValue({
							index,
							value:
								row.value.value.toLowerCase() === "true" ? "false" : "true",
						}),
					),
				),
			]);
		case "number":
			return h.input([
				h.Type("number"),
				h.Class(base),
				h.AriaLabel(i18next.t("attributes.numberValue")),
				h.Value(row.value.value),
				onInput,
			]);
		case "date":
			return h.input([
				h.Type("date"),
				h.Class(base),
				h.AriaLabel(i18next.t("attributes.dateValue")),
				h.Value(row.value.value.split("T")[0] ?? ""),
				onInput,
			]);
		case "url": {
			const href = safeUrl(row.value.value);
			return h.div(
				[h.Class("flex items-center gap-1 w-full")],
				[
					h.input([
						h.Type("url"),
						h.Class(base),
						h.AriaLabel(i18next.t("attributes.urlValue")),
						h.Placeholder("https://"),
						h.Value(row.value.value),
						onInput,
					]),
					href !== undefined
						? h.a(
								[
									h.Href(href),
									h.Target("_blank"),
									h.DataAttribute(
										"analytics-event",
										namedInteractions.attributeLink,
									),
									h.Rel("noopener noreferrer"),
									h.Class("btn btn-ghost btn-xs"),
									h.AriaLabel(i18next.t("attributes.openUrl")),
								],
								["↗"],
							)
						: h.empty,
				],
			);
		}
		default:
			return h.input([
				h.Type("text"),
				h.Class(base),
				h.AriaLabel(i18next.t("attributes.textValue")),
				h.Value(row.value.value),
				onInput,
			]);
	}
};

export type ViewConfig<ParentMessage> = Readonly<{
	rows: ReadonlyArray<Row>;
	schema: ReadonlyArray<AttributeSchemaEntry>;
	toParentMessage: (message: Message) => ParentMessage;
}>;

/** The editable row list with the "+ Add attribute" button. */
export const view = <ParentMessage>(
	h: HtmlBuilder<ParentMessage>,
	{ rows, schema, toParentMessage: to }: ViewConfig<ParentMessage>,
): Html => {
	const schemaKeys = new Set(schema.map((entry) => entry.key));
	return h.div(
		[h.Class("space-y-2")],
		[
			...rows.map((row, index) =>
				h.keyed("div")(
					String(index),
					[h.Class("flex items-center gap-2")],
					[
						h.input([
							h.Type("text"),
							h.Class("input input-bordered input-sm w-36"),
							h.AriaLabel(i18next.t("attributes.key")),
							h.Placeholder(i18next.t("attributes.keyPlaceholder")),
							h.Readonly(!row.isNew && schemaKeys.has(row.key)),
							h.Value(row.key),
							h.OnInput((value) => to(Message.ChangedKey({ index, value }))),
						]),
						h.select(
							[
								h.Class("select select-bordered select-sm w-24"),
								h.AriaLabel(i18next.t("attributes.type")),
								h.Value(row.type),
								h.OnChange((value) =>
									to(
										Message.ChangedType({
											index,
											value:
												ValueType.literals.find((v) => v === value) ?? "string",
										}),
									),
								),
							],
							ValueType.literals.map((type) =>
								h.option(
									[h.Value(type), h.Selected(row.type === type)],
									[type],
								),
							),
						),
						h.div([h.Class("flex-1")], [valueInput(h, row, index, to)]),
						h.button(
							[
								h.Type("button"),
								h.Class("btn btn-ghost btn-xs"),
								h.AriaLabel(i18next.t("attributes.remove")),
								h.OnClick(to(Message.ClickedRemove({ index }))),
							],
							[Trash2(h, { size: 14 })],
						),
					],
				),
			),
			h.button(
				[
					h.Type("button"),
					h.Class("btn btn-ghost btn-sm"),
					h.AriaLabel(i18next.t("attributes.add")),
					h.OnClick(to(Message.ClickedAdd())),
				],
				[Plus(h, { size: 14 }), i18next.t("attributes.add")],
			),
		],
	);
};
